<template>
    <Loader v-if="loading"/>
    <div v-else>
        <div
            v-if="contracts.length === 0 && customContracts.length === 0 && !hasLimitedAccess && getTenantAccess === null"
            class="inner contracts no-content">
            <div class="headline">
                <h1>{{ $t('contracts.overview.headline') }}</h1>
            </div>
            <div class="no-content-box">
                <div>
                    <h2>{{ $t('contracts.none.headline') }}</h2>
                    <p>{{ $t('contracts.none.text') }}</p>
                    <router-link disabled to="#" class="button green">
                        <SvgIcon icon="plus-circle"/>
                        {{ $t('contracts.add.button') }}
                    </router-link>
                    <p v-if="getTenantAccess === null && getSubscriptionId > 1">
                        Upload your own PDF and run it in default signing flow
                    </p>
                    <router-link v-if="getTenantAccess === null && getSubscriptionId > 1"
                                 to="#"
                                 disabled
                                 class="button green">
                        <SvgIcon icon="plus-circle"/>
                        Upload
                    </router-link>
                </div>
            </div>
        </div>
        <div v-else class="inner contracts with-content with-tables">
            <div class="headline">
                <h1>{{ $t('contracts.overview.headline') }}</h1>
            </div>
            <tabs>

                <tab
                    v-if="!hasLimitedAccess"
                    :name="$t('contracts.tabs.all')"
                    id="contracts-all"
                >
                    <ContractsList
                        v-on:deleted="deleted"
                        v-on:filter="load"
                        headline="contracts.overview.all.headline"
                        :data="allContracts"
                    />

                    <custom-contract-list
                        v-on:filter="load"
                        v-if="getTenantAccess !== null && customContracts.length"
                        :data="customContracts"
                        :controls="false"
                        :header="false"
                    />
                </tab>
                <tab
                    v-if="!hasLimitedAccess"
                    :name="$t('contracts.tabs.drafts')"
                    id="contracts-draft"
                >
                    <ContractsList
                        v-on:deleted="deleted"
                        v-on:filter="load"
                        :data="contractsInProgress"
                        headline="contracts.overview.draft.headline"
                    />
                </tab>
                <tab name="Afventer" id="contracts-in-progress">
                    <tabs class="with-subtabs">
                        <tab :name="$t('contracts.tabs.all-awaiting')"
                             id="contracts-in-progress-all">
                            <ContractsList
                                v-on:deleted="deleted"
                                :data="contractsAwaitingAll"
                                headline="contracts.overview.all-awaiting.headline"
                            />
                        </tab>
                        <tab :name="$t('contracts.tabs.waiting-approval')"
                             id="contracts-in-progress-awaiting-approval">
                            <ContractsList
                                v-on:deleted="deleted"
                                :data="contractsAwaitingApproval"
                                headline="contracts.overview.waiting-approval.headline"
                            />
                        </tab>
                        <tab :name="$t('contracts.tabs.awaiting-signature')"
                             id="contracts-in-progress-awaiting-signature">
                            <ContractsList
                                v-on:deleted="deleted"
                                :data="contractsAwaitingSignature"
                                headline="contracts.tabs.awaiting-signature"
                            />
                        </tab>
                        <!-- <tab v-if="!hasLimitedAccess"
                             :name="$t('contracts.tabs.awaitingtenantsignature')"
                             id="contracts-in-progress-awaiting-tenant-signature">
                            <ContractsList v-on:deleted="deleted" :data="contractsAwaitingTenantSignature"
                                           headline="contracts.overview.awaitingtenantsignature.headline"/>
                        </tab>
                        <tab v-if="true" :name="$t('contracts.tabs.awaitinglandlordsignature')">
                            <ContractsList v-on:deleted="deleted" :data="contractsAwaitingLandlordSignature"
                                           id="contracts-in-progress-awaiting-landlord-signature"
                                           headline="contracts.overview.awaitinglandlordsignature.headline"/>
                        </tab> -->
                    </tabs>
                </tab>

                <tab v-if="getTenantAccess === null && !hasLimitedAccess"
                     :name="$t('contracts.tabs.signed')"
                     id="contracts-completed"
                >
                    <ContractsList
                        v-on:deleted="deleted()"
                        v-on:filter="load"
                        :data="contractsSigned"
                        headline="contracts.overview.signed.headline"
                    />
                </tab>

                <tab v-if="getTenantAccess === null && !hasLimitedAccess && subscriptionHasLegacyContracts"
                     :name="$t('contracts.tabs.legacy')"
                     id="legacy-contracts"
                >
                    <ContractsList
                        v-on:deleted="deleted()"
                        v-on:filter="load"
                        :data="legacyContracts"
                        headline="contracts.tabs.legacy"
                        description="contracts.tabs.legacy-desc"
                        class="legacy-section"
                    />
                </tab>

                <tab
                    v-if="!hasLimitedAccess"
                    :name="$t('contracts.tabs.custom-contracts')"
                    id="custom-contracts"
                >
                    <custom-contract-list
                        v-on:deleted="deleted()"
                        v-on:filter="loadCustom"
                        :data="customContracts"
                        :controls="!hasLimitedAccess && getSubscriptionId > 1"
                    />
                </tab>
            </tabs>
        </div>
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import SvgIcon from '../../components/Svg.vue';
import Loader from '../../components/Loader.vue';
import ContractsList from '../../components/ContractsList.vue';
import parser from '../../mixins/parser.js';
import CustomContractList from "../custom-contracts/CustomContractList";
import draftContractMixin from '../../mixins/draft-contract';
import createEntity from "../../mixins/create-entity";
export default {
    name: "LandlordContracts",
    components: {
        CustomContractList,
        SvgIcon,
        ContractsList,
        Loader,
    },
    mixins: [draftContractMixin, createEntity],
    data: () => ({
        contracts: null,
        customContracts: null,
    }),
    computed: {
        ...mapGetters([
            'subscriptionHasLegacyContracts',
            'hasLimitedAccess',
            'getToken',
            'getSubscriptionId',
            'getTenantAccess',
        ]),
        loading() {
            return this.contracts === null || this.customContracts === null;
        },
        legacyContracts() {
            return this.allContracts.filter(contract => contract.legacy);
        },
        contractsAwaitingLandlordSignature() {
            return this.allContracts.filter(contract => contract._status == 'awaitinglandlordsignature');
        },
        contractsAwaitingTenantSignature() {
            return this.allContracts.filter(contract => contract._status == 'awaitingtenantsignature');
        },
        contractsSigned() {
            return this.allContracts.filter(contract => contract._status == 'completed');
        },
        contractsInProgress() {
            return this.allContracts.filter(contract => contract._status == 'inprogress');
        },
        contractsAwaitingApproval() {
            return this.allContracts.filter(contract => contract._status == 'awaitingapproval');
        },
        contractsAwaitingSignature() {
            return this.allContracts.filter(
                contract => contract._status == 'awaitinglandlordsignature'
                    || contract._status == 'awaitingtenantsignature'
                    || contract._status == 'ready'
            );
        },
        contractsAwaitingAll() {
            return this.allContracts.filter(contract =>
                contract._status == 'awaitingapproval'
                || contract._status == 'awaitingtenantsignature'
                || contract._status == 'awaitinglandlordsignature'
                || contract._status == 'ready');
        },
        allContracts() {
            return this.contracts;
        }
    },
    methods: {
        deleted() {
            this.load();
            this.loadCustom();
        },
        loadCustom(query = '', orderBy = '') {
            axios({
                method: 'GET',
                url: this.$root.apiUrl + 'api/custom-contract?sort=' + orderBy + '&query=' + query,
                cancelToken: window.AuthorizationCancelSource.token,
            }).then((response) => {
                this.customContracts = response.data.data
            });
        },
        load(query = '', orderBy = '') {
            this.contracts = [];

            axios({
                method: 'GET',
                url: this.$root.apiUrl + 'api/draft-contract?sort=' + orderBy + '&query=' + query,
                cancelToken: window.AuthorizationCancelSource.token,
            }).then((response) => {
                this.parseDraftContracts(response.data)
                this.draftContractsResponse = response.data;
            });

            axios({
                method: 'GET',
                url: this.$root.apiUrl + 'api/legacy-contract?sort=' + orderBy + '&query=' + query,
                cancelToken: window.AuthorizationCancelSource.token,
            }).then((response) => {
                this.parseLegacyContracts(response.data)
                this.legacyContractsResponse = response.data;
            });
        },
        getTenantName(contract) {

            if (contract.tenants.length === 0) {
                return this.$t('properties.table.contracts.tenantnotselected');
            }

            let name = contract.tenants[0].name || contract.tenants[0].email;

            if (contract.tenants.length > 1) {
                name += ' + ' + (contract.tenants.length - 1);
            }

            return name;
        },
        parseDraftContracts(data) {
            this.contracts = this.contracts.concat(data.map(row => {
                row.tenant_name = this.getTenantName(row);
                row.expires_on = row.occupancyDate ? moment(String(row.occupancyDate)).format('DD-MM-YYYY') : '';
                row.status = this.$t('properties.table.contracts.' + this.getDraftContractStatus(row));
                row._status = this.getDraftContractStatus(row);
                row.link = this.getDraftContractRoute(row);

                return this.parseContract(row, false);
            }));
        },
        parseLegacyContracts(data) {
            this.contracts = this.contracts.concat(data.map(row => {
                row.expires_on = row.expires_on ? moment(String(row.expires_on)).format('DD-MM-YYYY') : '';
                row.link = window.config.apiUrl + 'api/legacy-contract/' + row.id + '?token=' + this.getToken;
                return this.parseContract(row, true);
            }));
        },
        parseContract(row, isLegacy) {
            row.address = parser.extractStreet(row.property.address);
            row.city = row.property.zip_code + ' ' + row.property.city;
            row.legacy = isLegacy;
            return row;
        }
    },
    created() {
        this.load();
        this.loadCustom();

        window.document.addEventListener('language-change', () => {
            this.contracts = [];
            this.parseDraftContracts(this.draftContractsResponse);
            this.parseLegacyContracts(this.legacyContractsResponse);
        })
    },
}
</script>
